<script setup>
import { WdsBadge, WdsIcon } from '@wds/components';

const props = defineProps({
  checkInTime: {
    type: Object,
    required: true,
  },
  checkOutTime: {
    type: Object,
    required: true,
  },
  additionalRules: {
    type: Array,
    required: false,
    default: () => [],
  },
  otherHouseRules: {
    type: Array,
    required: false,
    default: () => null,
  },
});

const allHouseRules = computed(() => {
  const houseRulesList = Array.isArray(props.otherHouseRules) ? props.otherHouseRules : props.additionalRules;

  if (!Array.isArray(houseRulesList)) {
    return [];
  }

  return houseRulesList.map((rule) => {
    if (typeof rule === 'string') {
      return { title: rule };
    }
    return rule;
  });
});

const { getFacilityIcon } = useFacilities();
</script>

<template>
  <div class="stay-policy-container">
    <div class="checkin-checkout-rules">
      <div class="checkin-time">
        <WdsIcon
          asset="check-in"
          color="green"
          width="24"
          height="24"
        />
        <span>
          <strong>{{ $t('t_RWDCHECKIN') }}</strong>
          <p>{{ checkInTime?.startsAt }} - {{ checkInTime?.endsAt }}</p>
        </span>
      </div>
      <div class="checkout-time">
        <WdsIcon
          asset="check-out"
          color="red"
          width="24"
          height="24"
        />
        <span>
          <strong>{{ $t('t_RWDCHECKOUT') }}</strong>
          <p>{{ $t('t_UNTIL') }} {{ checkOutTime?.endsAt }}</p>
        </span>
      </div>
    </div>
    <ul class="additional-rule-list">
      <li
        v-for="rule in allHouseRules"
        :key="rule.title"
        class="additional-rule-item"
      >
        <WdsBadge
          :icon="rule.icon || getFacilityIcon(rule.title)"
          class="additional-rule-badge"
          theme="blue-light"
          size="large"
        />
        <div class="additional-rule-description">
          <span>{{ $t(`t_${rule.title}`) }}</span>
          <p v-if="rule.subtitleKey || rule.subtitle">
            {{ rule.subtitleKey ? $t(rule.subtitleKey, rule.subtitleParams || {}) : rule.subtitle }}
          </p>
        </div>
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.stay-policy-container {
  display: flex;
  flex-direction: column;
  gap: $wds-spacing-m;
  container-type: inline-size;
  container-name: stay-policy-container;

  .checkin-checkout-rules {
    display: inline-flex;
    justify-content: flex-start;
    padding-bottom: $wds-spacing-m;
    border-bottom: 1px solid $wds-color-ink-lighter;

    @include body-2-reg;

    .checkin-time,
    .checkout-time {
      display: inline-flex;
      align-items: center;
      gap: $wds-spacing-s;
      white-space: nowrap;
    }

    .checkin-time {
      padding-right: $wds-spacing-m;
    }

    .checkout-time {
      padding-left: $wds-spacing-m;
      border-left: 1px solid $wds-color-ink-lighter;
    }
  }

  .additional-rule-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: $wds-spacing-m $wds-spacing-xl;
    align-items: flex-start;
    width: 100%;

    .additional-rule-item {
      display: inline-flex;
      gap: $wds-spacing-s;
      align-items: center;

      .additional-rule-badge {
        padding: $wds-spacing-s;
        border-radius: 50%;
      }

      .additional-rule-description {
        display: flex;
        flex-direction: column;
        flex: 1;

        > span {
          @include body-2-reg;

          overflow-wrap: anywhere;
        }

        > p {
          @include body-3-reg;

          color: $wds-color-ink;
        }
      }
    }
  }

  @include desktop-large {
    flex-direction: row;

    .checkin-checkout-rules {
      flex-direction: column;
      padding: $wds-spacing-s $wds-spacing-m 0 0;
      border-bottom: none;
      border-right: 1px solid $wds-color-ink-lighter;

      .checkin-time {
        padding-bottom: $wds-spacing-m;
        padding-inline: 0;
      }

      .checkout-time {
        padding-inline: 0;
        border: none;
      }
    }
  }

  @container stay-policy-container (width >= 720px) {
    .additional-rule-list {
      grid-template-columns: repeat(3, 1fr);
    }
  }

  @container stay-policy-container (width >= 1024px) {
    .additional-rule-list {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}
</style>
